import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { Message, translator } from 'mage-i18n';
import { SpacerVertical } from '@klarna/bubble-ui';
import { MOET_CONSTANTS, payloadUtils, productsUtils } from 'moet-core';

import { Row, Column } from 'mage-components';

import { CY_PRODUCT_PAGE_IDS, CY_PRODUCT_TO_CONTAINER_IDS_MAP } from './constants';
import { generateCyElemId, isRunningContextCypress } from 'moet-test-utils/cypress/utils';

import { CAPS, APP_PATHS } from '@/constants';
import { withCapabilityBeforeNext } from '@/flow/common';
import { isKustomPartner } from '@/selectors/data';
import {
  getUserMerchantId,
  getDataForAddingProducts,
  getMerchantProductsWithStatus,
  hasNonKlarnaOneProducts,
  merchantRegion,
} from '@/selectors/merchant';
import {
  getSelectedProductId,
  getSelectedProductOptionIds,
  getProductsError,
  isKlarnaOneAvailable,
} from '@/selectors/products';
import { isAddingProducts } from '@/selectors/loadingStates';
import {
  CallToActionButton,
  ContainerTemplate,
  LinkWithoutHref,
  ProductSelectionWrapper,
} from '@/components';
import { logEvent } from '@/utils/analytics';

import { ANALYTICS_EVENTS } from './analyticsEvents';

const { PRODUCT_PRICING_TYPES } = MOET_CONSTANTS;

const tPrefix = 'products-fe.containers.select_products';
const tPrefixSignupProducts = 'products-fe.containers.signup_products';

export class ProductSelection extends React.Component {
  constructor(props) {
    super(props);

    const {
      products,
      selectedProductId,
      selectedProductOptionIds,
      isKlarnaOneAvailable,
    } = this.props;

    this.state = {
      products: products.reduce(
        (acc, product) => [
          ...acc,
          ...(product.pricing === PRODUCT_PRICING_TYPES.PARENT
            ? [
                {
                  id: product.id,
                  isInstalled: product.status === 'INSTALLED',
                  isSelected: product.id === selectedProductId,
                  group: product.id,
                },
              ]
            : product.options.map(option => ({
                id: option.id,
                isInstalled: option.status === 'INSTALLED',
                isSelected:
                  !!selectedProductOptionIds.find(po => po.id === option.id) ||
                  // preselects mandatory product on the page load
                  productsUtils.isProductMandatory(option.id, this.props.country, {
                    isKlarnaOneAvailable,
                  }),
                group: product.id,
              }))),
        ],
        [],
      ),
    };

    this.onNext = this.onNext.bind(this);
    this.openPricing = this.openPricing.bind(this);
    this.isSelectedFn = this.isSelectedFn.bind(this);
    this.hasSelectedProducts = this.hasSelectedProducts.bind(this);
    this.isInstalledFn = this.isInstalledFn.bind(this);
    this.onSelect = this.onSelect.bind(this);
  }

  onSelect(id) {
    const currentState = this.state.products.find(p => p.id === id);
    const { isKlarnaOneAvailable } = this.props;

    // If It's the current product and it's not selected, we should select it
    // it's from the current group and it's selected, it should remain selected
    // else should be unselected
    this.setState({
      products: this.state.products.map(product => ({
        ...product,
        isSelected:
          (product.id === id && !currentState.isSelected) ||
          (product.id !== id && product.group === currentState.group && product.isSelected) ||
          // mandatory product should always stay selected
          productsUtils.isProductMandatory(product.id, this.props.country, {
            isKlarnaOneAvailable,
          }),
      })),
    });
  }

  hasSelectedProducts() {
    return this.state.products.some(p => p.isSelected && !p.isInstalled);
  }

  onNext() {
    const { merchantId, onNext } = this.props;
    const selectedProducts = this.state.products.filter(p => p.isSelected && !p.isInstalled);
    const groups = selectedProducts.reduce(
      (acc, currProduct) => ({
        ...acc,
        [currProduct.group]: {
          id: currProduct.group,
          options:
            currProduct.id === currProduct.group
              ? undefined
              : [
                  ...((acc[currProduct.group] && acc[currProduct.group].options) || []),
                  { id: currProduct.id },
                ],
        },
      }),
      {},
    );

    onNext(payloadUtils.getMerchantCapabilityPayload(merchantId, {}, () => Object.values(groups)));
  }

  openPricing() {
    logEvent(ANALYTICS_EVENTS.clickPricingTable);
    const { history, basePath } = this.props;
    history.push({
      pathname: `${basePath}/${APP_PATHS.PRICING_PATH}`,
      search: history.location.search,
      state: { referrer: `${basePath}/${APP_PATHS.PRODUCT_SELECTION_PATH}` },
    });
  }

  isSelectedFn(id) {
    return this.state.products.find(p => p.id === id)?.isSelected || false;
  }

  isInstalledFn(id) {
    return this.state.products.find(p => p.id === id)?.isInstalled || false;
  }

  render() {
    const {
      country,
      isAddingProducts,
      products,
      referrer,
      t,
      region,
      error,
      hasNonKlarnaOneProductsSelected,
      isKlarnaOneAvailable,
      isKustomPartner,
    } = this.props;
    const kustomProducts = products.filter(p => p.id.startsWith('kco') && p.isCompatible);
    const hasKustomProducts = kustomProducts.length > 0;
    const klarnaProducts = products.filter(p => p.id.startsWith('kp') && p.isCompatible);
    let filteredProducts = products;
    if (isKustomPartner) {
      filteredProducts = hasKustomProducts ? kustomProducts : [];
    } else {
      filteredProducts = klarnaProducts;
    }

    const isKlarnaOne = isKlarnaOneAvailable && !hasNonKlarnaOneProductsSelected;
    const compatibleOneProducts = filteredProducts.filter(
      p => p.id.includes('one') && p.isCompatible,
    );
    const shouldShowExpandedProductCard = compatibleOneProducts.length === 1;
    const translationSuffix = isKlarnaOne && shouldShowExpandedProductCard ? '_kco_hidden' : '';
    return (
      <ContainerTemplate
        title={
          isKustomPartner
            ? t(`${tPrefixSignupProducts}.kustom.title`)
            : t(`${tPrefix}.title${translationSuffix}`)
        }
        subtitle={
          isKustomPartner
            ? t(`${tPrefixSignupProducts}.kustom.subtitle${translationSuffix}`)
            : t(`${tPrefix}.subtitle${translationSuffix}`)
        }
        error={error}
      >
        <ProductSelectionWrapper
          t={t}
          onChange={this.onSelect}
          country={country}
          products={filteredProducts}
          isAddingProducts={isAddingProducts}
          isSelectedFn={this.isSelectedFn}
          isInstalledFn={this.isInstalledFn}
          extraAttributesToProductFn={product =>
            isRunningContextCypress(window)
              ? {
                  'data-is-selected': product.isSelected ? 'true' : 'false',
                  ...generateCyElemId(CY_PRODUCT_TO_CONTAINER_IDS_MAP[product.id], window),
                }
              : {}
          }
          isKlarnaOne={isKlarnaOne}
          shouldShowExpandedProductCard={shouldShowExpandedProductCard}
        />
        <Row alignItems="center" justify="flex-end">
          {region !== 'us' && (
            <Column columns={{ desktop: 8, mobile: 6 }} alignItems="flex-end">
              <LinkWithoutHref
                isUnderlined
                colorToken="colors/text/neutral"
                onPress={() => this.openPricing(referrer)}
              >
                <Message path={`products-fe.general.pricing_link_${region}`} />
              </LinkWithoutHref>
            </Column>
          )}
          <Column columns={{ desktop: region === 'us' ? 12 : 4, mobile: region === 'us' ? 12 : 6 }}>
            <CallToActionButton
              id="selected-products-continue"
              onClick={this.onNext}
              loading={isAddingProducts}
              disabled={isAddingProducts || !this.hasSelectedProducts()}
              {...generateCyElemId(CY_PRODUCT_PAGE_IDS.BTN_CONTINUE, window)}
            >
              {t(`${tPrefix}.buttons.next`)}
            </CallToActionButton>
          </Column>
        </Row>
        <SpacerVertical spaceToken="space/150" />
      </ContainerTemplate>
    );
  }
}

ProductSelection.propTypes = {
  basePath: PropTypes.string.isRequired,
  country: PropTypes.string.isRequired,
  hasNonKlarnaOneProductsSelected: PropTypes.bool.isRequired,
  history: PropTypes.object.isRequired,
  isAddingProducts: PropTypes.bool.isRequired,
  isKlarnaOneAvailable: PropTypes.bool.isRequired,
  merchantId: PropTypes.string.isRequired,
  products: PropTypes.array.isRequired,
  referrer: PropTypes.string.isRequired,
  region: PropTypes.oneOf(['eu', 'us']).isRequired,
  t: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  error: PropTypes.bool,
  isKustomPartner: PropTypes.bool,
  selectedProductId: PropTypes.string,
  selectedProductOptionIds: PropTypes.array,
};

export default compose(
  connect((state, { basePath }) => ({
    dataForAddingProducts: getDataForAddingProducts(state),
    isAddingProducts: isAddingProducts(state),
    isKlarnaOneAvailable: isKlarnaOneAvailable(state),
    isKustomPartner: isKustomPartner(state),
    hasNonKlarnaOneProductsSelected: hasNonKlarnaOneProducts(state),
    merchantId: getUserMerchantId(state),
    products: getMerchantProductsWithStatus(state),
    error: getProductsError(state),
    referrer: `${basePath}/${APP_PATHS.PRODUCT_SELECTION_PATH}`,
    selectedProductId: getSelectedProductId(state),
    selectedProductOptionIds: getSelectedProductOptionIds(state),
    t: translator(state),
    region: merchantRegion(state),
  })),
  withCapabilityBeforeNext({ cap: CAPS.V2_MERCHANTS_PRODUCTS_PUT }, ({ payload }) => {
    const productId = payload.body
      .map(({ id, options = [] }) => (id === 'kp' ? options.map(({ id }) => id).join(',') : id))
      .join(',');
    logEvent(ANALYTICS_EVENTS.selectProduct, { productId });
  }),
)(ProductSelection);
